import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Save, AlertTriangle, MessageCircle, Lock, Star, CheckCircle2 } from 'lucide-react';

interface AdminWhatsAppProps {
  db: any;
  getCollectionPath: (col: string) => string;
  userId: string;
  subscriptionPlan?: string;
}

export function AdminWhatsApp({ db, getCollectionPath, userId, subscriptionPlan = 'Basico' }: AdminWhatsAppProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    is_whatsapp_enabled: false,
    receive_whatsapp_requests: false,
    send_whatsapp_reminders: false,
    whatsapp_reminder_hours: 24,
    auto_fixed_requests: false,
    auto_fixed_requests_hours: 24,
    auto_fixed_requests_minutes: 0
  });

  const isFullPlus = subscriptionPlan === 'Full Plus';

  useEffect(() => {
    async function loadData() {
      if (!isFullPlus) {
        setLoading(false);
        return;
      }
      try {
        const docRef = doc(db, getCollectionPath('privateClubSettings'), userId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          setFormData({
            is_whatsapp_enabled: data.is_whatsapp_enabled || false,
            receive_whatsapp_requests: data.receive_whatsapp_requests || false,
            send_whatsapp_reminders: data.send_whatsapp_reminders || false,
            whatsapp_reminder_hours: data.whatsapp_reminder_hours || 24,
            auto_fixed_requests: data.auto_fixed_requests || false,
            auto_fixed_requests_hours: data.auto_fixed_requests_hours !== undefined ? data.auto_fixed_requests_hours : 24,
            auto_fixed_requests_minutes: data.auto_fixed_requests_minutes || 0
          });
        }
      } catch (err: any) {
        console.error(err);
        setError('Error al cargar la configuración de WhatsApp.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [userId, db, getCollectionPath, isFullPlus]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFullPlus) return;
    
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await setDoc(doc(db, getCollectionPath('privateClubSettings'), userId), {
        is_whatsapp_enabled: formData.is_whatsapp_enabled,
        receive_whatsapp_requests: formData.receive_whatsapp_requests,
        send_whatsapp_reminders: formData.send_whatsapp_reminders,
        whatsapp_reminder_hours: formData.whatsapp_reminder_hours,
        auto_fixed_requests: formData.auto_fixed_requests,
        auto_fixed_requests_hours: formData.auto_fixed_requests_hours,
        auto_fixed_requests_minutes: formData.auto_fixed_requests_minutes
      }, { merge: true });
      setSuccess('Configuración guardada correctamente.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error(err);
      setError('Error al guardar la configuración.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-10 text-center animate-pulse text-white">Cargando configuración de WhatsApp...</div>;
  }

  if (!isFullPlus) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-6 bg-[#1a1c23] rounded-3xl border border-white/10 mt-4 shadow-2xl relative overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent pointer-events-none" />
        <div className="w-24 h-24 bg-[#25D366]/10 rounded-full flex items-center justify-center mb-6 border border-[#25D366]/20 relative">
          <MessageCircle size={40} className="text-[#25D366]" />
          <div className="absolute -bottom-2 -right-2 bg-[#1a1c23] rounded-full p-1 border border-white/10">
            <Lock size={16} className="text-zinc-400" />
          </div>
        </div>
        <h2 className="text-2xl font-black uppercase tracking-tight text-white mb-2">
          Notificaciones por WhatsApp
        </h2>
        <p className="text-white/60 text-sm max-w-md mb-8">
          Envía confirmaciones automáticas de reservas directamente al celular de tus clientes. 
          Esta función premium está disponible exclusivamente en el <strong>Plan Full Plus</strong>.
        </p>
        <button onClick={() => alert('Próximamente: Redirección a pasarela de pago para Full Plus')} className="bg-[#25D366] text-black font-black uppercase tracking-widest text-xs px-8 py-4 rounded-xl hover:scale-105 transition-all flex items-center gap-2 shadow-lg shadow-[#25D366]/20">
          <Star size={16} /> Mejorar a Full Plus
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in slide-in-from-right-4 p-4 md:p-0">
      <form onSubmit={handleSave} className="bg-[#1a1c23] rounded-[var(--radius-large)] p-6 md:p-8 shadow-2xl relative space-y-8">
        
        <div className="flex items-center gap-4 border-b border-[#2d313f] pb-6">
          <div className="w-14 h-14 bg-[#25D366]/10 rounded-2xl flex items-center justify-center border border-[#25D366]/20">
            <MessageCircle size={28} className="text-[#25D366]" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-white tracking-tight uppercase">Integración de WhatsApp</h2>
            <p className="text-sm text-zinc-400">Notifica automáticamente confirmaciones o cancelaciones de turnos</p>
          </div>
        </div>

        {/* ACTIVACIÓN */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-[#15171d] border border-[#2d313f] rounded-2xl p-6 gap-4">
          <div>
            <label className="text-sm font-black text-white cursor-pointer select-none">
              Activar Notificaciones de Cancelación/Confirmación
            </label>
            <p className="text-xs text-zinc-400 mt-1">Envía notificaciones a los jugadores cuando se confirma o cancela un turno.</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input 
              type="checkbox" 
              className="sr-only peer"
              checked={formData.is_whatsapp_enabled}
              onChange={(e) => setFormData({...formData, is_whatsapp_enabled: e.target.checked})}
            />
            <div className="w-14 h-7 bg-[#2d313f] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#25D366]"></div>
          </label>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-[#15171d] border border-[#2d313f] rounded-2xl p-6 gap-4">
          <div>
            <label className="text-sm font-black text-white cursor-pointer select-none">
              Recibir Notificaciones de Solicitudes Pendientes
            </label>
            <p className="text-xs text-zinc-400 mt-1">Recibe un WhatsApp en el teléfono del club cuando un jugador envía una solicitud de reserva.</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input 
              type="checkbox" 
              className="sr-only peer"
              checked={formData.receive_whatsapp_requests}
              onChange={(e) => setFormData({...formData, receive_whatsapp_requests: e.target.checked})}
            />
            <div className="w-14 h-7 bg-[#2d313f] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#25D366]"></div>
          </label>
        </div>

        <div className="flex flex-col gap-4 bg-[#15171d] border border-[#2d313f] rounded-2xl p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <label className="text-sm font-black text-white cursor-pointer select-none">
                Enviar Recordatorios de Asistencia
              </label>
              <p className="text-xs text-zinc-400 mt-1">Envía un mensaje automático al jugador para recordarle su turno próximo.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0">
              <input 
                type="checkbox" 
                className="sr-only peer"
                checked={formData.send_whatsapp_reminders}
                onChange={(e) => setFormData({...formData, send_whatsapp_reminders: e.target.checked})}
              />
              <div className="w-14 h-7 bg-[#2d313f] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#25D366]"></div>
            </label>
          </div>
          
          {formData.send_whatsapp_reminders && (
            <div className="mt-4 pt-4 border-t border-[#2d313f]">
              <label className="block text-xs font-black uppercase tracking-wider text-zinc-400 mb-2">Tiempo de Antelación</label>
              <select
                value={formData.whatsapp_reminder_hours}
                onChange={(e) => setFormData({...formData, whatsapp_reminder_hours: Number(e.target.value)})}
                className="w-full sm:w-1/2 bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3 focus:border-[#25D366] outline-none text-white text-sm"
              >
                <option value={2}>2 horas antes</option>
                <option value={4}>4 horas antes</option>
                <option value={8}>8 horas antes</option>
                <option value={12}>12 horas antes</option>
                <option value={24}>24 horas antes</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 bg-[#15171d] border border-[#2d313f] rounded-2xl p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <label className="text-sm font-black text-white cursor-pointer select-none">
                Automatizar Consultas en Turnos Fijos
              </label>
              <p className="text-xs text-zinc-400 mt-1">Envía una consulta automática de asistencia al jugador antes de cada turno fijo.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0">
              <input 
                type="checkbox" 
                className="sr-only peer"
                checked={formData.auto_fixed_requests}
                onChange={(e) => setFormData({...formData, auto_fixed_requests: e.target.checked})}
              />
              <div className="w-14 h-7 bg-[#2d313f] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#25D366]"></div>
            </label>
          </div>
          
          {formData.auto_fixed_requests && (
            <div className="mt-4 pt-4 border-t border-[#2d313f]">
              <label className="block text-xs font-black uppercase tracking-wider text-zinc-400 mb-2">Tiempo exacto de Antelación</label>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-1/2">
                <div className="flex-1">
                  <span className="text-[10px] uppercase font-bold text-zinc-500 mb-1 block">Horas</span>
                  <input
                    type="number"
                    min="0"
                    max="168"
                    value={formData.auto_fixed_requests_hours}
                    onChange={(e) => setFormData({...formData, auto_fixed_requests_hours: Number(e.target.value)})}
                    className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3 focus:border-[#25D366] outline-none text-white text-sm"
                  />
                </div>
                <div className="flex-1">
                  <span className="text-[10px] uppercase font-bold text-zinc-500 mb-1 block">Minutos</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={formData.auto_fixed_requests_minutes}
                    onChange={(e) => setFormData({...formData, auto_fixed_requests_minutes: Number(e.target.value)})}
                    className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3 focus:border-[#25D366] outline-none text-white text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ACCIONES */}
        <div className="pt-8 flex flex-col items-center gap-4 border-t border-[#2d313f]">
          {error && <p className="text-red-400 text-sm font-bold bg-red-500/10 px-6 py-3 rounded-xl border border-red-500/20">{error}</p>}
          {success && <p className="text-[#25D366] text-sm font-bold bg-[#25D366]/10 px-6 py-3 rounded-xl border border-[#25D366]/20 flex items-center gap-2"><CheckCircle2 size={16}/> {success}</p>}
          
          <button
            disabled={saving}
            type="submit"
            className="w-full md:w-auto min-w-[300px] bg-[#25D366] text-black font-black px-10 py-4 rounded-2xl transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-3 uppercase tracking-widest text-xs"
          >
            {saving ? (
              <div className="w-5 h-5 border-4 border-black/20 border-t-black rounded-full animate-spin" />
            ) : (
              <>
                <Save size={18} />
                Guardar Configuración
              </>
            )}
          </button>
        </div>

      </form>
    </div>
  );
}
